/**
 * markdownStrip.js — RC 1.50 R.2-polish
 *
 * Tiny zero-dependency markdown-token stripper for preview surfaces.
 *
 * Purpose: turn raw markdown like `# Heading`, `**bold**`, `* item`
 * into readable plain-prose text WHILE preserving whitespace,
 * line-breaks, and indentation so it still renders cleanly inside
 * a `<pre className="whitespace-pre-wrap">` block.
 *
 * NOT a full markdown renderer:
 *   - no bold weights, no heading sizes, no italics, no link colours
 *   - no nested-list rendering
 *   - no tables
 *   - no code-block fences
 * Those need a real markdown library (react-markdown / marked).
 * Intentionally deferred per RC 1.50 R.2 hard rails (no new deps,
 * no full renderer, no export work).
 *
 * Hard rails:
 *   - Pure function. No I/O. No imports. No React. No browser APIs.
 *   - Idempotent on plain prose (no markdown tokens → input unchanged).
 *   - Returns the input verbatim for non-string / empty input.
 *
 * @param {string} input
 * @returns {string}
 */
export function stripMarkdownTokens(input) {
  if (typeof input !== 'string' || input.length === 0) return input;
  return input
    // Headers: `# Heading` / `## Heading` → `Heading` (preserve text)
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    // Bullets: `- item` / `* item` / `+ item` → `• item` (preserve indent)
    .replace(/^([ \t]*)[-*+][ \t]+/gm, '$1• ')
    // Horizontal rules on their own line → drop the marker line content
    .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '')
    // Inline links `[text](url)` → `text`
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    // Inline code `` `text` `` → `text`
    .replace(/`([^`]+)`/g, '$1')
    // Bold + italic: `***text***` / `___text___` → `text`
    .replace(/(\*\*\*|___)(.+?)\1/g, '$2')
    // Bold: `**text**` / `__text__` → `text`
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    // Italic: `*text*` / `_text_` → `text` (single-token, non-greedy,
    // bounded to a single line so list bullets are unaffected)
    .replace(/(?<![*_])([*_])([^*_\n]+?)\1(?![*_])/g, '$2');
}

export default stripMarkdownTokens;
